/**
 * Sidebar Navigation Component
 * Provides navigation menu with role-based visibility
 * Enhanced with enterprise-grade styling and grouped sections
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  Collapse,
  ListSubheader,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  Map as MapIcon,
  Analytics as AnalyticsIcon,
  QueryBuilder as QueryIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  CloudDownload as InfluxIcon,
  Storage as DynamoIcon,
  ExpandLess,
  ExpandMore,
  Visibility as ViewIcon,
  Storage as DatabaseIcon,
  AdminPanelSettings as AdminIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import { useStore } from '../store/store';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES, ROUTE_PERMISSIONS } from '../shared/constants/routes';

const DRAWER_WIDTH = 280;

// Navigation menu items grouped by category
const navigationGroups = [
  {
    title: 'Overview',
    icon: <ViewIcon />,
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, route: ROUTES.DASHBOARD },
      { text: 'Devices', icon: <DevicesIcon />, route: ROUTES.DEVICES },
      { text: 'Map View', icon: <MapIcon />, route: ROUTES.MAP },
    ],
  },
  {
    title: 'Analytics & Queries',
    icon: <AnalyticsIcon />,
    items: [
      { text: 'Analytics', icon: <AnalyticsIcon />, route: ROUTES.ANALYTICS },
      { text: 'Queries', icon: <QueryIcon />, route: ROUTES.QUERIES },
    ],
  },
  {
    title: 'Data Sources',
    icon: <DatabaseIcon />,
    items: [
      { text: 'InfluxDB', icon: <InfluxIcon />, route: ROUTES.INFLUXDB },
      { text: 'DynamoDB', icon: <DynamoIcon />, route: ROUTES.DYNAMODB },
      { text: 'Excel Files', icon: <ExcelIcon />, route: ROUTES.EXCEL },
    ],
  },
  {
    title: 'Administration',
    icon: <AdminIcon />,
    items: [
      { text: 'Users', icon: <PeopleIcon />, route: ROUTES.USERS },
      { text: 'Settings', icon: <SettingsIcon />, route: ROUTES.SETTINGS },
    ],
  },
];

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarOpen = useStore((state) => state.sidebarOpen);
  const { userRole } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState({
    'Overview': true,
    'Analytics & Queries': true,
    'Data Sources': true,
    'Administration': true,
  });

  // Check if user can access a route
  const canAccessRoute = (route) => {
    const allowedRoles = ROUTE_PERMISSIONS[route] || [];
    return allowedRoles.includes(userRole);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const toggleGroup = (groupTitle) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  // Check if any item in a group is active
  const isGroupActive = (items) => {
    return items.some((item) => location.pathname === item.route);
  };

  return (
    <Drawer
      variant="persistent"
      open={sidebarOpen}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#ffffff',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Toolbar
        sx={{
          backgroundColor: 'transparent',
          color: 'text.primary',
          minHeight: '64px !important',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
          SR Enterprise
        </Typography>
      </Toolbar>
      
      <Box sx={{ overflow: 'auto', pt: 1 }}>
        {navigationGroups.map((group) => {
          // Filter items user can access
          const accessibleItems = group.items.filter((item) => canAccessRoute(item.route));
          
          // Skip group if no accessible items
          if (accessibleItems.length === 0) {
            return null;
          }

          const isGroupExpanded = expandedGroups[group.title];
          const hasActiveItem = isGroupActive(accessibleItems);

          return (
            <List
              key={group.title}
              component="nav"
              subheader={
                <ListSubheader
                  component="div"
                  onClick={() => toggleGroup(group.title)}
                  sx={{
                    backgroundColor: 'transparent',
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                    {group.icon}
                  </Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                      color: hasActiveItem ? 'primary.main' : 'text.secondary',
                      flexGrow: 1,
                    }}
                  >
                    {group.title}
                  </Typography>
                  {isGroupExpanded ? (
                    <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                </ListSubheader>
              }
              sx={{ px: 1 }}
            >
              <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {accessibleItems.map((item) => {
                    const isActive = location.pathname === item.route;

                    return (
                      <ListItem key={item.text} disablePadding>
                        <ListItemButton
                          selected={isActive}
                          onClick={() => handleNavigation(item.route)}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            px: 2,
                            py: 1.25,
                            ml: 1,
                            '&.Mui-selected': {
                              backgroundColor: (theme) => `${theme.palette.primary.main}12`,
                              color: 'primary.main',
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: (theme) => `${theme.palette.primary.main}20`,
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                            },
                            '&:hover': {
                              backgroundColor: 'grey.50',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActive ? 'primary.main' : 'text.secondary',
                              minWidth: 40,
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.9375rem',
                              letterSpacing: '-0.01em',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </List>
          );
        })}
      </Box>
    </Drawer>
  );
}

export default Sidebar;
